import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Group,
  Assessment,
  LocationOn,
  Timeline,
  TrendingUp,
  MonitorHeart
} from '@mui/icons-material';

// Fix for default markers in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom icon for users with data
const userWithDataIcon = new L.Icon({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'user-with-data-marker'
});

// Custom icon for users without data
const userWithoutDataIcon = new L.Icon({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [20, 32],
  iconAnchor: [10, 32],
  popupAnchor: [1, -30],
  shadowSize: [32, 32],
  className: 'user-without-data-marker'
});

const UKMap = () => {
  const [mapData, setMapData] = useState({
    usersWithData: [],
    usersWithoutData: [],
    statistics: {
      totalUsers: 0,
      usersWithData: 0,
      usersWithoutData: 0,
      averageReadingsPerUser: 0,
      topRegions: []
    }
  });
  const [showUsersWithData, setShowUsersWithData] = useState(true);
  const [showUsersWithoutData, setShowUsersWithoutData] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [showHeatmap, setShowHeatmap] = useState(false);

  // Mock data for demonstration
  useEffect(() => {
    const mockData = {
      usersWithData: [
        {
          id: 1,
          name: 'John D.',
          city: 'London',
          position: [51.5074, -0.1278],
          readingsCount: 45,
          lastReading: '2025-01-08',
          avgSystolic: 125,
          avgDiastolic: 82,
          hypertensionRisk: 'Low'
        },
        {
          id: 2,
          name: 'Sarah M.',
          city: 'Manchester',
          position: [53.4808, -2.2426],
          readingsCount: 38,
          lastReading: '2025-01-07',
          avgSystolic: 135,
          avgDiastolic: 88,
          hypertensionRisk: 'Medium'
        },
        {
          id: 3,
          name: 'David W.',
          city: 'Birmingham',
          position: [52.4862, -1.8904],
          readingsCount: 52,
          lastReading: '2025-01-08',
          avgSystolic: 118,
          avgDiastolic: 76,
          hypertensionRisk: 'Low'
        },
        {
          id: 4,
          name: 'Emma L.',
          city: 'Leeds',
          position: [53.8008, -1.5491],
          readingsCount: 29,
          lastReading: '2025-01-06',
          avgSystolic: 142,
          avgDiastolic: 92,
          hypertensionRisk: 'High'
        },
        {
          id: 5,
          name: 'Michael R.',
          city: 'Glasgow',
          position: [55.8642, -4.2518],
          readingsCount: 41,
          lastReading: '2025-01-08',
          avgSystolic: 128,
          avgDiastolic: 84,
          hypertensionRisk: 'Low'
        },
        {
          id: 6,
          name: 'Lisa K.',
          city: 'Liverpool',
          position: [53.4084, -2.9916],
          readingsCount: 33,
          lastReading: '2025-01-07',
          avgSystolic: 130,
          avgDiastolic: 85,
          hypertensionRisk: 'Medium'
        },
        {
          id: 7,
          name: 'James T.',
          city: 'Edinburgh',
          position: [55.9533, -3.1883],
          readingsCount: 47,
          lastReading: '2025-01-08',
          avgSystolic: 122,
          avgDiastolic: 79,
          hypertensionRisk: 'Low'
        },
        {
          id: 8,
          name: 'Rachel G.',
          city: 'Cardiff',
          position: [51.4816, -3.1791],
          readingsCount: 26,
          lastReading: '2025-01-05',
          avgSystolic: 138,
          avgDiastolic: 89,
          hypertensionRisk: 'Medium'
        },
        {
          id: 9,
          name: 'Tom B.',
          city: 'Bristol',
          position: [51.4545, -2.5879],
          readingsCount: 35,
          lastReading: '2025-01-07',
          avgSystolic: 115,
          avgDiastolic: 74,
          hypertensionRisk: 'Low'
        },
        {
          id: 10,
          name: 'Hannah P.',
          city: 'Newcastle',
          position: [54.9783, -1.6178],
          readingsCount: 42,
          lastReading: '2025-01-08',
          avgSystolic: 133,
          avgDiastolic: 87,
          hypertensionRisk: 'Medium'
        }
      ],
      usersWithoutData: [
        {
          id: 11,
          name: 'Alex C.',
          city: 'Sheffield',
          position: [53.3811, -1.4701],
          registrationDate: '2025-01-05',
          daysSinceRegistration: 3
        },
        {
          id: 12,
          name: 'Sophie W.',
          city: 'Nottingham',
          position: [52.9548, -1.1581],
          registrationDate: '2025-01-03',
          daysSinceRegistration: 5
        },
        {
          id: 13,
          name: 'Ryan M.',
          city: 'Leicester',
          position: [52.6369, -1.1398],
          registrationDate: '2025-01-04',
          daysSinceRegistration: 4
        },
        {
          id: 14,
          name: 'Chloe H.',
          city: 'Coventry',
          position: [52.4068, -1.5197],
          registrationDate: '2025-01-06',
          daysSinceRegistration: 2
        },
        {
          id: 15,
          name: 'Ben J.',
          city: 'Hull',
          position: [53.7457, -0.3367],
          registrationDate: '2025-01-07',
          daysSinceRegistration: 1
        }
      ],
      statistics: {
        totalUsers: 15,
        usersWithData: 10,
        usersWithoutData: 5,
        averageReadingsPerUser: 38.8,
        topRegions: [
          { region: 'London', users: 3, avgReadings: 42 },
          { region: 'Manchester', users: 2, avgReadings: 35 },
          { region: 'Scotland', users: 2, avgReadings: 44 },
          { region: 'Midlands', users: 4, avgReadings: 34 },
          { region: 'North', users: 4, avgReadings: 39 }
        ]
      }
    };
    setMapData(mockData);
  }, []);

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'Low': return '#4caf50';
      case 'Medium': return '#ff9800';
      case 'High': return '#f44336';
      default: return '#2196f3';
    }
  };

  const getCircleOptions = (user) => {
    const baseOptions = {
      radius: Math.max(user.readingsCount * 2, 100),
      fillOpacity: 0.3,
      weight: 2
    };

    if (user.hypertensionRisk) {
      return {
        ...baseOptions,
        color: getRiskColor(user.hypertensionRisk),
        fillColor: getRiskColor(user.hypertensionRisk)
      };
    }

    return {
      ...baseOptions,
      color: '#9e9e9e',
      fillColor: '#9e9e9e'
    };
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Grid container spacing={3} sx={{ height: '100%' }}>
        {/* Map Statistics */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom>
              UK User Distribution
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Card sx={{ textAlign: 'center', p: 1 }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="h4" color="primary">
                      {mapData.statistics.totalUsers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Users
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{ textAlign: 'center', p: 1 }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="h4" color="success.main">
                      {mapData.statistics.usersWithData}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      With Data
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{ textAlign: 'center', p: 1 }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="h4" color="warning.main">
                      {mapData.statistics.usersWithoutData}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No Data Yet
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{ textAlign: 'center', p: 1 }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="h4" color="info.main">
                      {mapData.statistics.averageReadingsPerUser.toFixed(1)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Readings
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Map Controls
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={showUsersWithData}
                  onChange={(e) => setShowUsersWithData(e.target.checked)}
                  color="success"
                />
              }
              label="Users with Data"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={showUsersWithoutData}
                  onChange={(e) => setShowUsersWithoutData(e.target.checked)}
                  color="warning"
                />
              }
              label="Users without Data"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={showHeatmap}
                  onChange={(e) => setShowHeatmap(e.target.checked)}
                  color="primary"
                />
              }
              label="Show Heatmap"
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Top Regions
            </Typography>
            
            {mapData.statistics.topRegions.map((region, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">{region.region}</Typography>
                  <Chip 
                    size="small" 
                    label={`${region.users} users`}
                    color="primary"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Avg: {region.avgReadings} readings
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Map */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ height: '600px', overflow: 'hidden' }}>
            <MapContainer
              center={[54.5, -4.5]} // Center on UK
              zoom={6}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Users with data */}
              {showUsersWithData && mapData.usersWithData.map((user) => (
                <React.Fragment key={user.id}>
                  <Marker
                    position={user.position}
                    icon={userWithDataIcon}
                  >
                    <Popup>
                      <Box sx={{ p: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {user.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <LocationOn sx={{ fontSize: 16, mr: 0.5 }} />
                          {user.city}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <MonitorHeart sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="body2">
                            {user.readingsCount} readings
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Assessment sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="body2">
                            {user.avgSystolic}/{user.avgDiastolic} mmHg avg
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />
                          <Chip 
                            size="small" 
                            label={`${user.hypertensionRisk} Risk`}
                            color={user.hypertensionRisk === 'Low' ? 'success' : user.hypertensionRisk === 'Medium' ? 'warning' : 'error'}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Last reading: {user.lastReading}
                        </Typography>
                      </Box>
                    </Popup>
                  </Marker>
                  
                  {/* Show heatmap circles */}
                  {showHeatmap && (
                    <Circle
                      center={user.position}
                      {...getCircleOptions(user)}
                    />
                  )}
                </React.Fragment>
              ))}
              
              {/* Users without data */}
              {showUsersWithoutData && mapData.usersWithoutData.map((user) => (
                <Marker
                  key={user.id}
                  position={user.position}
                  icon={userWithoutDataIcon}
                >
                  <Popup>
                    <Box sx={{ p: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {user.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <LocationOn sx={{ fontSize: 16, mr: 0.5 }} />
                        {user.city}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" color="warning.main">
                        No readings yet
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Registered: {user.registrationDate}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {user.daysSinceRegistration} days ago
                      </Typography>
                    </Box>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UKMap; 